import { useState, useEffect } from 'react';
import { TrendingUp, Zap, Server, Phone, Download, Share2 } from 'lucide-react';
import { useScrollAnimation } from '../hooks/useScrollAnimation';
import { trackCalculatorUse, trackDownload, trackShare } from '../utils/analytics';
import { useLanguage } from '../contexts/LanguageContext';
import { useLocation } from 'react-router-dom';
import { isEnglishPath } from '../utils/i18nRoutes';

export default function OptimizationCalculator() {
  const [energyCosts, setEnergyCosts] = useState(5000);
  const [itCosts, setItCosts] = useState(8000);
  const [telecomCosts, setTelecomCosts] = useState(2000);
  const { t } = useLanguage();
  const location = useLocation();
  const isEn = isEnglishPath(location.pathname);

  const { ref: calculatorRef, isVisible: calculatorVisible } = useScrollAnimation();

  useEffect(() => {
    const savedData = localStorage.getItem('optiaiCalculatorData');
    if (savedData) {
      const parsed = JSON.parse(savedData);
      setEnergyCosts(parsed.energyCosts || 5000);
      setItCosts(parsed.itCosts || 8000);
      setTelecomCosts(parsed.telecomCosts || 2000);
    }
  }, []);

  useEffect(() => {
    const data = { energyCosts, itCosts, telecomCosts };
    localStorage.setItem('optiaiCalculatorData', JSON.stringify(data));
    trackCalculatorUse({ energyCosts, itCosts, telecomCosts });
  }, [energyCosts, itCosts, telecomCosts]);

  const calculatePotential = () => {
    const energyOptimization = energyCosts * 0.15;
    const itOptimization = itCosts * 0.25;
    const telecomOptimization = telecomCosts * 0.20;

    const totalMonthly = energyOptimization + itOptimization + telecomOptimization;
    const totalYearly = totalMonthly * 12;

    const minPercent = 15;
    const maxPercent = 25;

    return {
      monthly: totalMonthly,
      yearly: totalYearly,
      minPercent,
      maxPercent,
      energySavings: energyOptimization * 12,
      itSavings: itOptimization * 12,
      telecomSavings: telecomOptimization * 12
    };
  };

  const potential = calculatePotential();

  const generatePDF = () => {
    trackDownload('optimization-analysis');
    const data = `
${t('calculator.pdf.title')}
=======================================

${t('calculator.pdf.expenses')}
${t('calculator.pdf.energy')} ${energyCosts.toLocaleString('de-DE')} €
${t('calculator.pdf.it')} ${itCosts.toLocaleString('de-DE')} €
${t('calculator.pdf.telecom')} ${telecomCosts.toLocaleString('de-DE')} €

${t('calculator.pdf.yearly')}
${potential.yearly.toLocaleString('de-DE')} €

${t('calculator.pdf.breakdown')}
${t('calculator.pdf.energy2')} ${potential.energySavings.toLocaleString('de-DE')} €
${t('calculator.pdf.it2')} ${potential.itSavings.toLocaleString('de-DE')} €
${t('calculator.pdf.telecom2')} ${potential.telecomSavings.toLocaleString('de-DE')} €

${t('calculator.pdf.note')}
${t('calculator.pdf.contact')}

${t('calculator.pdf.created')} ${new Date().toLocaleDateString('de-DE')}
    `;

    const blob = new Blob([data], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `OptiAI-Analyse-${new Date().toISOString().split('T')[0]}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const shareResults = () => {
    trackShare('link_copy');
    const url = new URL(window.location.href);
    url.searchParams.set('energy', energyCosts.toString());
    url.searchParams.set('it', itCosts.toString());
    url.searchParams.set('telecom', telecomCosts.toString());

    navigator.clipboard.writeText(url.toString()).then(() => {
      alert(t('calculator.share.copied'));
    });
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const energyParam = params.get('energy');
    const itParam = params.get('it');
    const telecomParam = params.get('telecom');

    if (energyParam) setEnergyCosts(Number(energyParam));
    if (itParam) setItCosts(Number(itParam));
    if (telecomParam) setTelecomCosts(Number(telecomParam));
  }, []);

  const eyebrow = isEn ? 'Potential' : 'Potenzial';
  const ctaLabel = t('calculator.cta');

  return (
    <section className="py-20 px-6 bg-dark dark:bg-gray-950">
      <div className="max-w-6xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Left column */}
          <div className="text-white">
            <span className="eyebrow text-accent-light">{eyebrow}</span>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mt-2 mb-4">
              {t('calculator.title')}
            </h2>
            <p className="text-lg text-gray-300 leading-relaxed mb-4">
              {t('calculator.subtitle')}
            </p>
            <p className="text-sm text-gray-400 leading-relaxed mb-6">
              {t('calculator.disclaimer')}
            </p>
            <a href="#anfrage" className="btn-ghost-dark text-base">
              {ctaLabel}
            </a>
          </div>

          {/* Right column: calculator card */}
          <div
            ref={calculatorRef}
            className={`bg-white dark:bg-gray-800 rounded-card shadow-xl border border-line dark:border-gray-700 p-8 ${
              calculatorVisible ? 'animate-fade-in-up' : 'opacity-0'
            }`}
          >
            <div className="space-y-6">
              <h3 className="text-lg font-bold text-dark dark:text-white mb-4">
                {t('calculator.expenses')}
              </h3>

              <div>
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-amber-50 dark:bg-amber-900/30 rounded-lg flex items-center justify-center">
                      <Zap className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                    </div>
                    <label className="font-semibold text-dark dark:text-white text-sm">{t('calculator.energy')}</label>
                  </div>
                  <span className="text-lg font-bold text-dark dark:text-white">
                    {energyCosts.toLocaleString('de-DE')} €
                  </span>
                </div>
                <input
                  type="range"
                  min="1000"
                  max="50000"
                  step="500"
                  value={energyCosts}
                  onChange={(e) => setEnergyCosts(Number(e.target.value))}
                  className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-amber-600 dark:accent-amber-500"
                />
                <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mt-1">
                  <span>1.000 €</span>
                  <span>50.000 €</span>
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-blue-50 dark:bg-blue-900/30 rounded-lg flex items-center justify-center">
                      <Server className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                    </div>
                    <label className="font-semibold text-dark dark:text-white text-sm">{t('calculator.it')}</label>
                  </div>
                  <span className="text-lg font-bold text-dark dark:text-white">
                    {itCosts.toLocaleString('de-DE')} €
                  </span>
                </div>
                <input
                  type="range"
                  min="2000"
                  max="100000"
                  step="1000"
                  value={itCosts}
                  onChange={(e) => setItCosts(Number(e.target.value))}
                  className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-blue-600 dark:accent-blue-500"
                />
                <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mt-1">
                  <span>2.000 €</span>
                  <span>100.000 €</span>
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-green-50 dark:bg-green-900/30 rounded-lg flex items-center justify-center">
                      <Phone className="w-5 h-5 text-green-600 dark:text-green-400" />
                    </div>
                    <label className="font-semibold text-dark dark:text-white text-sm">{t('calculator.telecom')}</label>
                  </div>
                  <span className="text-lg font-bold text-dark dark:text-white">
                    {telecomCosts.toLocaleString('de-DE')} €
                  </span>
                </div>
                <input
                  type="range"
                  min="500"
                  max="20000"
                  step="250"
                  value={telecomCosts}
                  onChange={(e) => setTelecomCosts(Number(e.target.value))}
                  className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-green-600 dark:accent-green-500"
                />
                <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mt-1">
                  <span>500 €</span>
                  <span>20.000 €</span>
                </div>
              </div>

              <div className="border-t border-line dark:border-gray-700 pt-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center">
                    <TrendingUp className="w-5 h-5 text-white" />
                  </div>
                  <h3 className="text-lg font-bold text-dark dark:text-white">
                    {t('calculator.potential')}
                  </h3>
                </div>

                <div className="bg-primary-tint dark:bg-gray-900/40 rounded-xl p-6">
                  <div className="text-muted dark:text-gray-300 mb-2 text-sm font-medium">{t('calculator.yearly')}</div>
                  <div className="text-3xl sm:text-4xl font-bold text-primary dark:text-accent-light mb-2">
                    {potential.yearly.toLocaleString('de-DE', { maximumFractionDigits: 0 })} €
                  </div>
                  <div className="text-xs text-muted dark:text-gray-400">
                    {t('calculator.corresponds')} {potential.minPercent}–{potential.maxPercent}{t('calculator.of')}
                  </div>

                  <div className="h-px bg-blue-200 dark:bg-gray-700 my-4"></div>

                  <div className="space-y-2">
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-muted dark:text-gray-300 flex items-center gap-2">
                        <Zap className="w-4 h-4 text-amber-600" />
                        {t('calculator.energy.short')}
                      </span>
                      <span className="font-bold text-dark dark:text-white">
                        {potential.energySavings.toLocaleString('de-DE', { maximumFractionDigits: 0 })} €
                      </span>
                    </div>
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-muted dark:text-gray-300 flex items-center gap-2">
                        <Server className="w-4 h-4 text-blue-600" />
                        {t('calculator.it')}
                      </span>
                      <span className="font-bold text-dark dark:text-white">
                        {potential.itSavings.toLocaleString('de-DE', { maximumFractionDigits: 0 })} €
                      </span>
                    </div>
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-muted dark:text-gray-300 flex items-center gap-2">
                        <Phone className="w-4 h-4 text-green-600" />
                        {t('calculator.telecom.short')}
                      </span>
                      <span className="font-bold text-dark dark:text-white">
                        {potential.telecomSavings.toLocaleString('de-DE', { maximumFractionDigits: 0 })} €
                      </span>
                    </div>
                  </div>

                  <div className="flex gap-3 mt-6">
                    <button
                      onClick={generatePDF}
                      className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 border-2 border-primary text-primary dark:text-accent-light rounded-full font-semibold text-sm hover:bg-primary-tint transition-colors"
                    >
                      <Download className="w-4 h-4" />
                      {t('calculator.export')}
                    </button>
                    <button
                      onClick={shareResults}
                      className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 border-2 border-primary text-primary dark:text-accent-light rounded-full font-semibold text-sm hover:bg-primary-tint transition-colors"
                    >
                      <Share2 className="w-4 h-4" />
                      {t('calculator.share')}
                    </button>
                  </div>
                </div>

                <p className="text-xs text-gray-400 dark:text-gray-500 mt-4 text-center">
                  {t('calculator.free')}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
